import { z } from 'zod';

import { HotelAmenitySchema } from '../shared/hotel_amenity_schema.js';
import { HotelPhotoSchema } from '../shared/hotel_photo_schema.js';
import { HotelOfferSchema, HotelRoomSchema } from './hotel_planner_schema.js';

// Exportadas para o projetor do BFF (`app-integration`) fatiar as listas do
// provedor ANTES do parse — sem isso, uma lista maior que o teto vira 502
// (fail-hard) em vez do fail-soft esperado (ver `toHotelDetailsInput`).
export const HOTEL_DETAILS_PHOTOS_MAX = 24;
export const HOTEL_DETAILS_AMENITIES_MAX = 64;
export const HOTEL_DETAILS_REVIEWS_MAX = 10;

/** Avaliação do Google como o provedor entrega: nota 1–5, tempo relativo JÁ
 *  formatado na fonte ("há 10 dias") e o texto. O nome só vira iniciais no avatar
 *  (o `profilePhotoUrl` é descartado no mapper do modal-session). */
const HotelDetailsReviewSchema = z.object({
  authorName: z.string().min(1),
  rating: z.number().min(1).max(5).optional(),
  relativeTimeDescription: z.string().min(1).optional(),
  text: z.string().min(1).optional(),
});
export type HotelDetailsReview = z.infer<typeof HotelDetailsReviewSchema>;

/**
 * Resposta do BFF `GET /hotel/details?offerId=` — só o hotel-level que o drawer de
 * detalhes mostra, incluindo os quartos que o drawer hidrata antes de liberar a
 * escolha de tarifa. A oferta do `hotel_planner` nunca torna quartos selecionáveis
 * por si só.
 * As listas têm `.default([])` para o consumidor nunca precisar de guarda de
 * ausência; `reviewCount` vive aqui (e não no bloco `hotel_planner`) porque o
 * `HotelOfferSchema` não carrega o campo.
 */
export const HotelDetailsResultSchema = z.object({
  description: z.string().min(1).optional(),
  reviewCount: z.number().int().nonnegative().optional(),
  photos: z.array(HotelPhotoSchema).max(HOTEL_DETAILS_PHOTOS_MAX).default([]),
  amenities: z.array(HotelAmenitySchema).max(HOTEL_DETAILS_AMENITIES_MAX).default([]),
  reviews: z.array(HotelDetailsReviewSchema).max(HOTEL_DETAILS_REVIEWS_MAX).default([]),
  /** A lista COMPLETA de quartos do hotel — toda tarifa de todo fornecedor.
   *  Sem teto de propósito: o detalhe é de UM hotel, e um teto aqui recusava a
   *  resposta cheia no navegador (`hotel_api.ts` parseia com `.parse()`). Ver
   *  `roomsToChatUi`, que alimenta este campo. */
  rooms: z.array(HotelRoomSchema).default([]),
  /** Tokens julgados ruído pelo dicionário (fase 2) — o navegador repassa ao
   *  buildHotelRoomsBlock. Aditivo/opcional: cliente antigo descarta. */
  noiseTokens: z.array(z.string().min(1).max(64)).max(24).optional(),
});
export type HotelDetailsResult = z.infer<typeof HotelDetailsResultSchema>;

/**
 * Resposta do BFF `GET /hotel/offer?offerId=` — a oferta (herói: nome, nota,
 * endereço, preço, foto) MAIS o `detail` hotel-level, resolvidos SÓ pelo handle
 * opaco da URL. Existe porque uma aba nova é sessão nova: o `offerId` é cunhado
 * fresco a cada busca (`mintId('hotel_offer_handle')`), então a lista da aba nova
 * NUNCA reconstrói o hotel por casamento — mas o handle resolve pelo dono no
 * servidor (~24h), e o BFF já compõe a oferta pública ao hidratar o detalhe. Esta
 * leitura devolve as duas metades juntas para a Página do Hotel montar herói +
 * seções em uma ida só (sem segundo round-trip: o cliente prima o cache com o
 * `detail`). Ver `/hotel/details`, que devolve só a metade `detail`.
 */
export const HotelOfferDetailResultSchema = z.object({
  offer: HotelOfferSchema,
  detail: HotelDetailsResultSchema,
});
export type HotelOfferDetailResult = z.infer<typeof HotelOfferDetailResultSchema>;
